import { MAP_COLORS } from "./palette";
import { TUNING } from "./tuning";

// The circuits: a closed loop of control points each, turned into a centreline
// sampled one world unit apart. Everything else — walls, laps, where a kart is
// on the road — is measured against those samples, so the sim and the renderer
// agree on the track without either knowing how it was drawn.

export type Rgb = [number, number, number];

export interface Theme {
  ground: Rgb;
  road: Rgb;
  wall: Rgb;
  wallTop: Rgb;
  sky: Rgb;
  /** The new look's zenith and rings of hills (palette.ts). */
  skyTop?: Rgb;
  hills?: [Rgb, Rgb];
  prop: "tree" | "pine" | "forest";
  /** A dirt road between low stones, rather than tarmac between walls and kerbs. */
  dirt?: boolean;
  /** Its edges lined with blocks of ice rather than stones (a dirt road only). */
  ice?: boolean;
  /** …or with low logs. */
  logs?: boolean;
}

/**
 * Things on the road, besides items, pads and the rest above:
 *   pool   a puddle: slows you while you splash through
 *   slush  blue slush ice, spilt from red cups: slippery as ice
 *   swarm  mosquitoes hovering over the road: they slow you, no twirl
 *   moose  wanders across the road and back: hit it and you twirl
 *   ball   a giant croquet ball, whacked across the road and back by two
 *          mallets: it knocks you aside
 *   timber a stack of cut logs across one side of the road: go round it,
 *          or clip its side and scrape past, or hit its end and stop dead
 *   speaker a speaker stack beside the road (lateral offset from the middle,
 *          past the wall): on every beat it throws karts near it away from it
 */
export type HazardKind = "pool" | "slush" | "swarm" | "moose" | "ball" | "timber" | "speaker";

export interface Hazard {
  kind: HazardKind;
  idx: number;
  lat: number;
  /** Seconds into a moose's or ball's round trip at time 0; hazards out of step with each other. */
  phase?: number;
}

export interface MapDef {
  key: string;
  name: string;
  theme: Theme;
  /** Control points, in world units, driven in this order — and how high the road is there, if not flat. */
  points: [number, number, number?][];
  /**
   * How wide the road is along the lap: half-widths at fractions of the lap,
   * joined by straight lines, and HALF_WIDTH outside the first and last, so
   * start and end a list with HALF_WIDTH to taper into it and out again.
   */
  widths?: [number, number][];
  /** Where the boost pads sit: fraction of the lap, and lateral offset. */
  boosts: [number, number][];
  /** Fractions of the lap with a row of item boxes across the road. */
  boxes: number[];
  /** Lakes, [x, z, radius]; one that reaches the road floods it. */
  lakes?: [number, number, number][];
  /**
   * Secret paths through the bushes: from and to (fractions), which side of the
   * road, and how fast a kart goes along it, as a share of its top speed (1 if
   * left out).
   */
  cuts?: [number, number, 1 | -1, number?][];
  /** Fractions of the lap where ducks cross: a family, and another a little further on going the other way. */
  ducks?: number[];
  /** Branches lying on the road, to jump: fraction of the lap, and lateral offset. */
  branches?: [number, number][];
  /** Snowmen standing on the road: fraction of the lap, and lateral offset. */
  snowmen?: [number, number][];
  /** Fractions of the lap where a skier crosses, and another a little further on going the other way. */
  skiers?: number[];
  /** Icy stretches, road-wide: from and to (fractions). */
  ice?: [number, number][];
  /** Snow ramps to jump off: fraction of the lap at the lip, and lateral offset. */
  ramps?: [number, number][];
  /** A wooden house by the road: fraction of the lap, and which side. */
  house?: [number, 1 | -1];
  /** Kubb blocks standing on the road, to knock over: fraction of the lap, lateral offset, and 1 for the king. */
  kubbs?: [number, number, 1?][];
  /** Hazards: kind, fraction of the lap, lateral offset. */
  hazards?: [HazardKind, number, number, number?][];
  /** The boost pads are croquet hoops: drive through, or hit a post. */
  wickets?: boolean;
  /** In the Alps: what crosses as a moose does is a cow, and the ball a snowball rolling down, no mallets. */
  alpine?: boolean;
  /** Scenery, in world units: where the campfire, cabin, DJ (facing south) and croquet lawn are. */
  campfire?: [number, number];
  cabin?: [number, number];
  dj?: [number, number];
  lawn?: [number, number];
  kubbPitch?: [number, number];
  /** Which way round it's driven, or with extra ramps: see variantOf. */
  variant?: Variant;
  /** Points for one to five stars, if not the usual (tuning.ts). */
  stars?: number[];
}

export interface Sample {
  x: number;
  z: number;
  /** How high the road is here. */
  y: number;
  /** Unit tangent, the way the race runs. */
  tx: number;
  tz: number;
  /** Unit normal to the driver's right: (-tz, tx). */
  nx: number;
  nz: number;
  /** How far the road reaches to either side of this sample: the map's `widths` here, else HALF_WIDTH. */
  half: number;
}

export interface Pad {
  idx: number;
  lat: number;
}

/** A shortcut: a straight path that leaves the road at sample `a` and rejoins it `skip` samples on. */
export interface Cut {
  a: number;
  skip: number;
  ax: number;
  az: number;
  bx: number;
  bz: number;
  /** Unit direction, and length. */
  dx: number;
  dz: number;
  len: number;
  /** Top speed along it, as a share of the usual. */
  speed: number;
}

export interface Track {
  map: MapDef;
  samples: Sample[];
  n: number;
  /** The widest the road gets: half a sample's `half` at most. */
  half: number;
  pads: Pad[];
  boxes: { x: number; z: number; y: number }[];
  lakes: [number, number, number][];
  cuts: Cut[];
  ducks: number[];
  branches: Pad[];
  snowmen: Pad[];
  skiers: number[];
  /** Icy stretches: from sample, and how many samples on. */
  ice: [number, number][];
  ramps: Pad[];
  kubbs: (Pad & { king: boolean })[];
  hazards: Hazard[];
}

/** Half the road's width. */
export const HALF_WIDTH = 7;
/** A boost pad's size: along the road, and across it. */
export const PAD_LENGTH = 7;
export const PAD_WIDTH = 4.5;
/** Half a shortcut's width: narrower than the road, just room for a kart. */
export const CUT_HALF = 3.2;
/** Its first few units, through the gap in the wall, are narrower still: a gate to thread. */
export const CUT_GATE = 1.9;
export const GATE_LENGTH = 6;
/** A shortcut's half width, this far along it. */
export const cutHalf = (along: number) => (along < GATE_LENGTH ? CUT_GATE : CUT_HALF);
/** A branch's width across the road. */
export const BRANCH_WIDTH = 5;
/** A snow ramp: along the road up to its lip, across it, and how high the lip is. */
export const RAMP_LENGTH = 6;
export const RAMP_WIDTH = 5;
export const RAMP_HEIGHT = 1.6;
/** A croquet hoop's posts, either side of its pad. */
export const POST_RADIUS = 0.3;
export const POOL_RADIUS = 2.4;
/** The slush patch: half its length along the road, and across it. */
export const SLUSH_LENGTH = 5;
export const SLUSH_WIDTH = 3.5;
export const SWARM_RADIUS = 3;
export const MOOSE_RADIUS = 1.5;
export const BALL_RADIUS = 1.2;
/** A timber stack: half its length along the road, and half its width across. */
export const TIMBER_LENGTH = 2;
export const TIMBER_WIDTH = 3;
/** How far off the road a moose or ball waits before crossing back. */
export const CROSSING_SPAN = HALF_WIDTH + 9;
/** Seconds a moose or ball takes to cross, and waits before crossing back. */
export const CROSSING = { moose: [6, 5], ball: [TUNING.ball.cross, 2.4] };

export const MAPS: MapDef[] = [
  {
    key: "forest",
    name: "Vittsjö",
    theme: {
      ...MAP_COLORS.forest,
      prop: "forest",
      dirt: true,
      logs: true,
    },
    // The last corner but one is a hairpin, and the run out of it a long straight into the last: both
    // laid out from segments (TRACK-IMPROVEMENTS.md, VIT-5) and sampled as points.
    points: [
      [0, 0], [74, 0], [130, 6], [176, 30], [196, 76], [176, 116], [136, 112], [110, 84],
      [76, 96], [58, 130], [84, 162], [120, 190], [106, 236], [50, 246], [14, 220], [-30, 232],
      [-64, 200], [-60, 156], [-56.4, 145.8], [-48.5, 138.6], [-38, 136], [-22, 136], [-4.8, 132.7], [9.8, 123],
      [15.8, 113.2], [16.6, 101.9], [14.3, 96.6], [9.9, 93], [-8.2, 84.5], [-26.3, 76], [-44.4, 67.5], [-62.5, 59],
      [-75.1, 49.7], [-83.1, 36.3], [-84.6, 25.2], [-81.1, 14.5], [-75.9, 9], [-68.6, 6.6], [-50, 4],
    ],
    // Hoops out of the corners — one into the plank ramp, one into a branch.
    boosts: [[0.028, 0], [0.137, 0], [0.227, -2], [0.393, 0], [0.587, 2], [0.825, -2], [0.948, 2]],
    // Through the trees on the right, past the timber stacks.
    cuts: [[0.126, 0.259, 1, 0.7]],
    boxes: [0.076, 0.213, 0.473, 0.86],
    // The lake, and the stream out of it across the start straight.
    lakes: [[55, 50, 28], [64, 24, 6], [65, 15, 6], [66, 6, 6], [67, -3, 6], [68, -12, 6], [69, -21, 6]],
    branches: [[0.35, 0], [0.54, -2.5], [0.554, 2.5], [0.7, 2.5], [0.735, -2.5], [0.962, 3], [0.975, -1]],
    // Plank ramps: over a puddle, over the slush, and over the croquet ball's line.
    ramps: [[0.151, 0], [0.518, 3], [0.84, -2.5]],
    // Kubb in a slalom down the straights, the king in the middle of each row.
    kubbs: [
      [0.096, 2.5], [0.101, -1, 1], [0.107, 3],
      [0.646, 3], [0.653, -2.5], [0.659, 1.5, 1],
    ],
    hazards: [
      ["pool", 0.162, 0], ["pool", 0.27, 3], ["pool", 0.284, -3], ["pool", 0.497, 2.5], ["pool", 0.511, -3],
      ["pool", 0.85, 3], ["pool", 0.868, 3],
      ["swarm", 0.331, 0],
      // One moose, wandering slowly at the entry of the lap's tightest corner.
      ["moose", 0.75, 0],
      // The croquet gauntlet past the lawn: three balls in a wave, a gap sweeping across the road.
      ["ball", 0.406, 0, 0], ["ball", 0.421, 0, 0.4], ["ball", 0.435, 0, 0.8],
      // Timber stacks, staggered, to weave between. The run from the last
      // corner through the start is left clear: the fight for the lead.
      ["timber", 0.175, 4], ["timber", 0.189, -4],
    ],
    wickets: true,
    campfire: [-79, 162],
    cabin: [0, 40],
    dj: [72, 272],
    lawn: [125, 145],
    kubbPitch: [110, -28],
  },
  {
    key: "meadow",
    name: "Rebæk Søpark",
    theme: {
      ...MAP_COLORS.meadow,
      prop: "tree",
      dirt: true,
    },
    points: [
      [0, 0], [60, -8], [118, 2], [160, 34], [172, 84], [180, 140], [166, 182], [132, 190],
      [118, 160], [122, 118], [96, 96], [52, 110], [20, 150], [-30, 168], [-78, 150], [-96, 112],
      [-80, 76], [-116, 62], [-146, 40], [-128, 10], [-86, 4], [-40, -6],
    ],
    // Out of the corners onto the straights — and one for getting through the ford.
    boosts: [[0.04, 0], [0.235, -2.5], [0.44, 2.5], [0.585, 0], [0.76, -2.5], [0.935, 2.5]],
    boxes: [0.15, 0.39, 0.62, 0.91],
    // The one at [62, 120] floods the road past the ducks at 0.5.
    lakes: [[62, 120, 24], [30, 50, 30], [-60, 212, 22], [215, 70, 18], [-150, 100, 16]],
    // Across the foot of the U, and across the hook on the west side.
    cuts: [[0.3, 0.41, 1, 0.6], [0.785, 0.875, 1, 0.5]],
    ducks: [0.1, 0.5, 0.6],
    // Fallen in threes and twos, staggered across the road.
    branches: [[0.2, 2], [0.215, -2.5], [0.47, -2.5], [0.685, -3.5], [0.7, 0], [0.715, 3]],
    // A pinch into the ford: 10 u across from 0.47 to 0.49, tapering over 15 u each side.
    widths: [[0.455, 7], [0.47, 5], [0.49, 5], [0.505, 7]],
    // A plank ramp over the ford: in the air you clear the flood, which only slows you on the ground.
    ramps: [[0.49, 0]],
    // A cabin outside the hook at the west end, so the tightest corner shows from far off.
    cabin: [-58, 78],
  },
  {
    key: "snow",
    name: "Val T",
    theme: {
      ...MAP_COLORS.snow,
      prop: "pine",
      dirt: true,
      ice: true,
    },
    // Up the mountain in steep switchbacks, over the top past the chalet and
    // the après-ski stage, and a plunge back down to the valley.
    points: [
      [0, 0, 0], [70, -4, 0], [125, 8, 1.5], [160, 40, 10], [150, 72, 18], [100, 76, 25],
      [55, 82, 31], [30, 105, 37], [55, 128, 43], [110, 126, 50], [160, 130, 57], [190, 160, 63],
      [180, 200, 68], [140, 215, 68], [90, 205, 57], [50, 225, 46], [0, 240, 36], [-50, 225, 38],
      [-80, 190, 41], [-70, 150, 32], [-100, 120, 23], [-140, 110, 24], [-160, 75, 19], [-130, 45, 11],
      [-90, 40, 6], [-60, 15, 2], [-35, 0, 0],
    ],
    // Thickest on the climb, to keep it from dragging.
    boosts: [
      [0.05, 0], [0.145, -2.5], [0.31, 0], [0.55, 0], [0.65, -2.5], [0.73, 2.5], [0.9, -2.5],
      [0.975, 0],
    ],
    boxes: [0.13, 0.33, 0.52, 0.93],
    snowmen: [[0.1, -3], [0.16, 3.5], [0.285, 3], [0.43, -3.5], [0.45, 3], [0.78, 3], [0.8, -3], [0.86, 0]],
    skiers: [0.25, 0.63, 0.83],
    ice: [[0.5, 0.535], [0.745, 0.775]],
    ramps: [[0.08, 0], [0.39, 0], [0.56, -2.5], [0.56, 2.5], [0.88, 0]],
    house: [0.47, -1],
    dj: [140, 240],
    hazards: [
      // Slush spilt from the après-ski, cows wandering over from the alp,
      // snowballs rolling down off the slopes, and timber stacked for the lifts.
      ["slush", 0.18, 0], ["slush", 0.715, 0],
      ["moose", 0.22, 0], ["moose", 0.67, 0],
      ["ball", 0.41, 0], ["ball", 0.845, 0],
      ["timber", 0.345, 4], ["timber", 0.358, -4], ["timber", 0.945, 4], ["timber", 0.958, -4],
    ],
    // Down the inside of the 77° corner on the descent: a steep way through, worth it with a boost.
    cuts: [[0.79, 0.87, 1, 0.75]],
    alpine: true,
  },
];

function catmullRom(p0: number, p1: number, p2: number, p3: number, t: number) {
  const t2 = t * t;
  const t3 = t2 * t;
  return 0.5 * (2 * p1 + (-p0 + p2) * t + (2 * p0 - 5 * p1 + 4 * p2 - p3) * t2 + (-p0 + 3 * p1 - 3 * p2 + p3) * t3);
}

export type Variant = "normal" | "reverse" | "trick";
export const VARIANTS: Record<Variant, string> = { normal: "Normal", reverse: "Baglæns", trick: "Trick" };

/** What a track is known by — its bests, its saved runs: its key, and its variant if it isn't the normal one. */
export const trackId = (map: MapDef) => (map.variant && map.variant !== "normal" ? `${map.key}-${map.variant}` : map.key);

/** How much further the stars are on the trick variant: there's more to score. */
const TRICK_STARS = 1.15;

/**
 * A track driven another way. Reversed: the same road the other way round,
 * everything on it where it was — so each fraction of the lap is one minus
 * what it was, and each side of the road the other side. Trick: the same
 * road with a ramp just past every boost pad, to fly off into a trick.
 */
export function variantOf(map: MapDef, variant: Variant): MapDef {
  if (variant === "normal") return map;
  if (variant === "trick") {
    // Past the pad by a pad and a ramp's length: fraction of the lap, from its length.
    const pts = map.points;
    const lap = pts.reduce((sum, p, i) => sum + Math.hypot(pts[(i + 1) % pts.length][0] - p[0], pts[(i + 1) % pts.length][1] - p[1]), 0);
    const after = (PAD_LENGTH + RAMP_LENGTH + 2) / lap;
    const extra = map.boosts.map(([f, lat]): [number, number] => [(f + after) % 1, lat]);
    return { ...map, variant, ramps: [...(map.ramps ?? []), ...extra], stars: TUNING.stars.map((at) => Math.round((at * TRICK_STARS) / 50) * 50) };
  }
  const f = (x: number) => (1 - x) % 1;
  const across = <T extends [number, number, ...unknown[]]>([x, lat, ...rest]: T) => [f(x), -lat, ...rest] as unknown as T;
  return {
    ...map,
    variant,
    // Same start, the other way round.
    points: [map.points[0], ...map.points.slice(1).reverse()],
    widths: map.widths?.map(([x, w]) => [f(x), w] as [number, number]),
    boosts: map.boosts.map(across),
    boxes: map.boxes.map(f),
    cuts: map.cuts?.map(([from, to, side, speed]) => [f(to), f(from), -side as 1 | -1, speed]),
    ducks: map.ducks?.map(f),
    branches: map.branches?.map(across),
    snowmen: map.snowmen?.map(across),
    skiers: map.skiers?.map(f),
    ice: map.ice?.map(([from, to]) => [f(to), f(from)]),
    ramps: map.ramps?.map(across),
    house: map.house && [f(map.house[0]), -map.house[1] as 1 | -1],
    kubbs: map.kubbs?.map(across),
    hazards: map.hazards?.map(([kind, x, lat, phase]) => [kind, f(x), -lat, phase]),
  };
}

/**
 * This week's challenge for the whole lodge: a track, which way round, and
 * what counts — the most points or the fastest time — always on medium, so
 * everyone's runs compare. The same for everyone, from the week (Monday to
 * Sunday, UTC): the tracks, variants and goals come round in turn.
 */
export function weeklyChallenge(now = Date.now()) {
  const DAY = 86400000;
  // 5 January 1970 was a Monday.
  const week = Math.floor((now - 4 * DAY) / (7 * DAY));
  const variants = Object.keys(VARIANTS) as Variant[];
  return {
    week,
    since: new Date(4 * DAY + week * 7 * DAY),
    mapKey: MAPS[week % MAPS.length].key,
    variant: variants[Math.floor(week / MAPS.length) % variants.length],
    difficulty: "medium" as const,
    goal: week % 2 ? ("time" as const) : ("score" as const),
  };
}
export type Challenge = ReturnType<typeof weeklyChallenge>;

export function buildTrack(map: MapDef): Track {
  const pts = map.points;
  const count = pts.length;
  // A dense polyline through the spline, then walked at a fixed step so one
  // sample is one unit of road wherever it is. Height rides along: it's
  // splined through the same points, but doesn't count towards the step.
  const dense: [number, number, number][] = [];
  for (let i = 0; i < count; i++) {
    const [a, b, c, d] = [-1, 0, 1, 2].map((o) => pts[(i + o + count) % count]);
    for (let s = 0; s < 64; s++) {
      const t = s / 64;
      const spline = (k: 0 | 1 | 2) => catmullRom(a[k] ?? 0, b[k] ?? 0, c[k] ?? 0, d[k] ?? 0, t);
      dense.push([spline(0), spline(1), spline(2)]);
    }
  }
  dense.push(dense[0]);

  const points: [number, number, number][] = [dense[0]];
  let carried = 0;
  for (let i = 1; i < dense.length; i++) {
    const [ax, az, ay] = dense[i - 1];
    const [bx, bz, by] = dense[i];
    const len = Math.hypot(bx - ax, bz - az);
    let at = 1 - carried;
    while (at <= len) {
      const t = at / len;
      points.push([ax + (bx - ax) * t, az + (bz - az) * t, ay + (by - ay) * t]);
      at += 1;
    }
    carried = len - (at - 1);
  }
  // The walk ends a fraction of a unit short of where it began, or exactly on
  // it; either way the last point is the first one again, so drop it.
  if (Math.hypot(points[points.length - 1][0] - points[0][0], points[points.length - 1][1] - points[0][1]) < 0.5) {
    points.pop();
  }

  const n = points.length;
  const samples: Sample[] = points.map(([x, z, y], i) => {
    const [px, pz] = points[(i - 1 + n) % n];
    const [qx, qz] = points[(i + 1) % n];
    const len = Math.hypot(qx - px, qz - pz) || 1;
    const tx = (qx - px) / len;
    const tz = (qz - pz) / len;
    return { x, z, y, tx, tz, nx: -tz, nz: tx, half: HALF_WIDTH };
  });
  // The road's width, from the map's list of half-widths along the lap.
  const widths = [...(map.widths ?? [])].sort((a, b) => a[0] - b[0]);
  const widthAt = (f: number) => {
    if (!widths.length || f < widths[0][0] || f >= widths[widths.length - 1][0]) return HALF_WIDTH;
    const next = widths.findIndex(([at]) => at > f);
    const [f0, w0] = widths[next - 1];
    const [f1, w1] = widths[next];
    return w0 + ((w1 - w0) * (f - f0)) / (f1 - f0);
  };
  samples.forEach((s, i) => (s.half = widthAt(i / n)));

  const pads = map.boosts.map(([f, lat]) => ({ idx: Math.floor(f * n), lat }));
  const boxes = map.boxes.flatMap((f) => {
    const s = samples[Math.floor(f * n)];
    return [-4.5, -1.5, 1.5, 4.5].map((row) => {
      const lat = (row * s.half) / HALF_WIDTH;
      return { x: s.x + s.nx * lat, z: s.z + s.nz * lat, y: s.y };
    });
  });

  const at = (f: number, lat: number) => {
    const s = samples[Math.floor(f * n) % n];
    return [s.x + s.nx * lat, s.z + s.nz * lat];
  };
  const halfNear = (f: number) => samples[Math.floor(f * n) % n].half;
  const cuts = (map.cuts ?? []).map(([from, to, side, speed = 1]): Cut => {
    // From just inside the road's edge to just inside it again, further on.
    const [ax, az] = at(from, side * (halfNear(from) - 1.5));
    const [bx, bz] = at(to, side * (halfNear(to) - 1.5));
    const len = Math.hypot(bx - ax, bz - az);
    const a = Math.floor(from * n);
    return { a, skip: (Math.floor(to * n) - a + n) % n, ax, az, bx, bz, dx: (bx - ax) / len, dz: (bz - az) / len, len, speed };
  });

  return {
    map,
    samples,
    n,
    half: Math.max(...samples.map((s) => s.half)),
    pads,
    boxes,
    lakes: map.lakes ?? [],
    cuts,
    ducks: (map.ducks ?? []).map((f) => Math.floor(f * n)),
    branches: (map.branches ?? []).map(([f, lat]) => ({ idx: Math.floor(f * n), lat })),
    snowmen: (map.snowmen ?? []).map(([f, lat]) => ({ idx: Math.floor(f * n), lat })),
    skiers: (map.skiers ?? []).map((f) => Math.floor(f * n)),
    ice: (map.ice ?? []).map(([from, to]): [number, number] => [Math.floor(from * n), Math.floor((to - from) * n)]),
    ramps: (map.ramps ?? []).map(([f, lat]) => ({ idx: Math.floor(f * n), lat })),
    kubbs: (map.kubbs ?? []).map(([f, lat, king]) => ({ idx: Math.floor(f * n), lat, king: !!king })),
    hazards: (map.hazards ?? []).map(([kind, f, lat, phase]) => ({ kind, idx: Math.floor(f * n), lat, phase })),
  };
}

/**
 * Seconds into a moose's or ball's round trip: across from the left side, and
 * back. Out of step by its `phase` if it has one, else by where it stands.
 */
export function crossingPhase(h: Hazard, t: number) {
  const [walk, wait] = CROSSING[h.kind as "moose" | "ball"];
  return (t + (h.phase ?? h.idx * 0.37)) % (2 * (walk + wait));
}

/**
 * How far across the road a hazard is at a time. A moose or a croquet ball
 * crosses from well off one side to well off the other, waits, and comes
 * back; a swarm drifts to and fro; everything else stays put.
 */
export function hazardLat(h: Hazard, t: number) {
  if (h.kind === "swarm") return h.lat + Math.sin(t * 0.6 + h.idx) * 3;
  if (h.kind !== "moose" && h.kind !== "ball") return h.lat;
  const [walk, wait] = CROSSING[h.kind];
  const p = crossingPhase(h, t);
  const lat = CROSSING_SPAN * (2 * Math.min(1, (p % (walk + wait)) / walk) - 1);
  return p < walk + wait ? lat : -lat;
}

/** How high the road is at a point near sample `idx`: between it and the next, or the one before. */
export function roadY(track: Track, idx: number, x: number, z: number) {
  const { samples, n } = track;
  const s = samples[idx];
  const along = (x - s.x) * s.tx + (z - s.z) * s.tz;
  const o = samples[(idx + (along < 0 ? n - 1 : 1)) % n];
  return s.y + (o.y - s.y) * Math.min(1, Math.abs(along));
}

/** Whether sample `idx` is on an icy stretch. */
export const onIce = (track: Track, idx: number) =>
  track.ice.some(([from, len]) => (idx - from + track.n) % track.n < len);

/** A lake's shore at an angle: a circle, wobbled so it doesn't look drawn with compasses. */
export function lakeRadius([x, z, r]: [number, number, number], angle: number) {
  return r * (1 + 0.08 * Math.sin(3 * angle + x) + 0.05 * Math.sin(5 * angle + z));
}

export function inWater(track: Track, x: number, z: number) {
  // A loop, not .some(): every kart asks this every step and every frame.
  for (const lake of track.lakes) if (Math.hypot(x - lake[0], z - lake[1]) < lakeRadius(lake, Math.atan2(z - lake[1], x - lake[0]))) return true;
  return false;
}

/** The shortcut nearest a point: how far along it (0…1), how far off its line, and how wide it is there. */
export function nearestCut(track: Track, x: number, z: number) {
  let best: { cut: Cut; t: number; dist: number; half: number; cx: number; cz: number } | null = null;
  for (const cut of track.cuts) {
    const along = Math.min(cut.len, Math.max(0, (x - cut.ax) * cut.dx + (z - cut.az) * cut.dz));
    const cx = cut.ax + cut.dx * along;
    const cz = cut.az + cut.dz * along;
    const dist = Math.hypot(x - cx, z - cz);
    if (!best || dist < best.dist) best = { cut, t: along / cut.len, dist, half: cutHalf(along), cx, cz };
  }
  return best;
}

/** How far the road reaches to either side of a sample. */
export const halfAt = (track: Track, idx: number) => track.samples[((idx % track.n) + track.n) % track.n].half;

/** The narrowest the road gets over `ahead` samples from `idx`: the room a driver can count on. */
export function narrowest(track: Track, idx: number, ahead: number) {
  let least = Infinity;
  for (let k = 0; k <= ahead; k++) least = Math.min(least, halfAt(track, idx + k));
  return least;
}

/** Shortest signed distance from sample a to sample b around the loop. */
export function loopDelta(a: number, b: number, n: number) {
  let d = (b - a) % n;
  if (d > n / 2) d -= n;
  if (d < -n / 2) d += n;
  return d;
}

/**
 * Nearest sample to a point, searched around the one it was nearest last time
 * — anything on the road moves at most a unit or two a step — and across the
 * whole loop only when that finds nothing close, as after a teleport.
 */
export function locate(track: Track, x: number, z: number, hint: number) {
  const { samples, n } = track;
  let best = nearestIn(track, x, z, hint - 24, hint + 24);
  const near = samples[best];
  if ((near.x - x) ** 2 + (near.z - z) ** 2 > (track.half + 6) ** 2) best = nearestIn(track, x, z, 0, n - 1);
  const s = samples[best];
  return { idx: best, lat: (x - s.x) * s.nx + (z - s.z) * s.nz, dist: Math.sqrt((s.x - x) ** 2 + (s.z - z) ** 2) };
}

/** The sample nearest (x, z) from `from` to `to`, wrapping round the loop. A plain function, not a closure: it runs for every kart, every step. */
function nearestIn({ samples, n }: Track, x: number, z: number, from: number, to: number) {
  let best = 0;
  let bestD = Infinity;
  for (let i = from; i <= to; i++) {
    const j = ((i % n) + n) % n;
    const s = samples[j];
    const d = (s.x - x) ** 2 + (s.z - z) ** 2;
    if (d < bestD) {
      bestD = d;
      best = j;
    }
  }
  return best;
}
